import { Dispatch, SetStateAction, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ProposalExtraItemType, WarehouseInfo } from '@econtractor/types';
import * as apiWarehouse from 'api/apiWarehouses';
import * as apiObject from 'api/apiObjects';
import { useToast } from 'context/ToastContext';
import { useSettings } from 'context/SettingsContext';
import { useAuth } from 'context/AuthContext';
import useSettingsParams from 'hooks/useSettingsParams';
import { useObjectCard } from '../../context/ObjectCardProvider';
import vconst from 'utils/helpers/structs/vconst';
import { allocateFromWarehouses } from './allocateFromWarehouses';
import { applyProposalSalePricesBatch } from './applyProposalSalePrice';
import { buildDemand } from './buildDemand';
import {
    addProposalExtraAsOtherExpend,
    hasMatchingOtherExpend,
    isProposalExtraAsOtherExpense,
} from './addProposalExtraAsOtherExpend';
import type {
    ProductPreviewRow,
    Proposal2WarehouseStep,
    Proposal2WarehouseTarget,
    ServicePreviewRow,
    WarehouseSourceKey,
    WarehouseSourceOption,
} from './types';

type TransferComingItem = {
    setId: number;
    product: {
        ID: number;
        value: number;
        label: string;
        vendor: string;
        article: string;
    };
    coming: {
        cCount: number;
        cDescription: string;
        price: number;
        priceSale: number;
        priceRate: number;
        priceSaleRate: number;
    };
};

const buildTransferItems = async (
    warehouseId: number,
    row: ProductPreviewRow,
    count: number,
    description: string,
    splitByPrice: boolean,
    rate: number,
): Promise<TransferComingItem[] | null> => {
    const product = {
        ID: row.productId,
        value: row.productId,
        label: row.prodName,
        vendor: row.vendorName || '',
        article: row.prodArticle || '',
    };

    // Роздрібна — завжди з КП; закупівельна — зі складу (якщо splitByPrice)
    const priceSale = Number(row.priceSale || 0);
    const priceSaleRate = priceSale * (Number(rate) || 1);

    if (!splitByPrice) {
        return [
            {
                setId: 0,
                product,
                coming: {
                    cCount: count,
                    cDescription: description,
                    price: 0,
                    priceSale,
                    priceRate: 0,
                    priceSaleRate,
                },
            },
        ];
    }

    const stockRows = (await apiWarehouse.getStockByProductId(warehouseId, row.productId)) || [];
    let remain = count;
    const items: TransferComingItem[] = [];

    for (const stockRow of stockRows) {
        if (remain <= 0) break;
        const stockCount = Number(stockRow?.stock || 0);
        if (stockCount <= 0) continue;
        const cCount = Math.min(remain, stockCount);
        remain -= cCount;
        items.push({
            setId: 0,
            product,
            coming: {
                cCount,
                cDescription: description,
                price: Number(stockRow?.operPrice || 0),
                priceSale,
                priceRate: Number(stockRow?.operPriceRate || 0),
                priceSaleRate,
            },
        });
    }

    return remain > 0 ? null : items;
};

interface UseProposal2WarehouseParams {
    showTarget: Proposal2WarehouseTarget | null;
    setShowTarget: Dispatch<SetStateAction<Proposal2WarehouseTarget | null>>;
}

const mapTransferError = (errCode: string | undefined, t: (key: string) => string) => {
    switch (errCode) {
        case 'NO_PRODUCT_STOCK':
            return t('documents.proposal2Warehouse.messNoStock');
        case 'WAREHOUSE_CLOSED':
            return t('documents.proposal2Warehouse.messWarehouseClosed');
        case 'TEAM_DELETED':
            return t('documents.proposal2Warehouse.messTeamDeleted');
        case 'CUSTOM_DELETED':
            return t('documents.proposal2Warehouse.messCustomDeleted');
        default:
            return t('common:messError');
    }
};

const useProposal2Warehouse = ({ showTarget, setShowTarget }: UseProposal2WarehouseParams) => {
    const { t } = useTranslation(['Objects', 'common', 'Warehouses']);
    const { showToast } = useToast();
    const { localSettings } = useSettings();
    const { auth } = useAuth();
    const params = useSettingsParams();
    const { vObject, fetchObject, objectId } = useObjectCard();

    const isManager = auth?.user?.access?.role === 'manager';
    const splitByPrice = Boolean(isManager && !params.ignoreWarehousePrice);
    const includeOtherInObjectCost = Boolean(params.includeOtherInObjectCost);

    const [step, setStep] = useState<Proposal2WarehouseStep>('sources');
    const [useReserve, setUseReserve] = useState(true);
    const [useTeam, setUseTeam] = useState(true);
    const [useCustom, setUseCustom] = useState(true);
    const [useMain, setUseMain] = useState(true);
    const [addServices, setAddServices] = useState(true);
    const [skipExisting, setSkipExisting] = useState(true);
    const [productRows, setProductRows] = useState<ProductPreviewRow[]>([]);
    const [serviceRows, setServiceRows] = useState<ServicePreviewRow[]>([]);
    const [forming, setForming] = useState(false);
    const [approving, setApproving] = useState(false);

    const objectWarehouseId = Number(vObject?.warehouseId || 0);
    const reservWarehouseId = Number(vObject?.reservWarehouseId || 0);
    const teamId = Number(vObject?.object?.teamId || 0);
    const teamName = String(vObject?.object?.teamName || '');
    const rate = Number(localSettings?.RATE?.current || 1);

    const teamWarehouseId = useMemo(() => {
        if (!teamId) return 0;
        const warehouses = (localSettings?.WAREHOUSES || []) as WarehouseInfo[];
        const teamWh = warehouses.find(
            (wh) =>
                Number(wh.teamId) === teamId &&
                !Number(wh.objectId) &&
                !Number(wh.reservObjectId),
        );
        return Number(teamWh?.ID || 0);
    }, [teamId, localSettings?.WAREHOUSES]);

    const customSourceOptions = useMemo((): WarehouseSourceOption[] => {
        if (!params.customWarehouses) return [];
        const warehouses = (localSettings?.WAREHOUSES || []) as WarehouseInfo[];
        return warehouses
            .filter((wh) => Number(wh.customId) > 0 && !Number(wh.customDeleted))
            .map((wh) => ({
                key: `custom_${wh.ID}` as WarehouseSourceKey,
                warehouseId: Number(wh.ID),
                label: wh.customName || `#${wh.customId}`,
                available: true,
            }));
    }, [params.customWarehouses, localSettings?.WAREHOUSES]);

    const sourceOptions = useMemo((): WarehouseSourceOption[] => {
        const options: WarehouseSourceOption[] = [
            {
                key: 'reserve',
                warehouseId: reservWarehouseId,
                label: t('documents.proposal2Warehouse.warehouseReserve'),
                available: reservWarehouseId > 0,
            },
            {
                key: 'team',
                warehouseId: teamWarehouseId,
                label: t('documents.proposal2Warehouse.warehouseTeam'),
                available: teamWarehouseId > 0,
            },
            {
                key: 'custom',
                warehouseId: customSourceOptions[0]?.warehouseId || 0,
                label: t('documents.proposal2Warehouse.warehouseCustom'),
                available: customSourceOptions.length > 0,
            },
            {
                key: 'main',
                warehouseId: vconst.waherouseMainId,
                label: t('documents.proposal2Warehouse.warehouseMain'),
                available: true,
            },
        ];
        return options;
    }, [reservWarehouseId, teamWarehouseId, customSourceOptions, t]);

    const switchLabels = useMemo(
        () => ({
            reserve: t('documents.proposal2Warehouse.switchReserve'),
            team: t('documents.proposal2Warehouse.switchTeam'),
            custom: t('documents.proposal2Warehouse.switchCustom'),
            main: t('documents.proposal2Warehouse.switchMain'),
        }),
        [t],
    );

    const switchHints = useMemo(
        () => ({
            reserve: t('documents.proposal2Warehouse.switchReserveHint'),
            team: t('documents.proposal2Warehouse.switchTeamHint'),
            custom: t('documents.proposal2Warehouse.switchCustomHint'),
            main: t('documents.proposal2Warehouse.switchMainHint'),
        }),
        [t],
    );

    const hasAdditionalItems = Boolean(showTarget?.additionalItems?.length);
    const hasProducts = Boolean(showTarget?.products?.length);

    const resetState = () => {
        setStep('sources');
        setUseReserve(true);
        setUseTeam(true);
        setUseCustom(true);
        setUseMain(true);
        setAddServices(true);
        setSkipExisting(true);
        setProductRows([]);
        setServiceRows([]);
        setForming(false);
        setApproving(false);
    };

    const handleHide = () => {
        setShowTarget(null);
        setTimeout(resetState, 300);
    };

    useEffect(() => {
        if (!showTarget) return;
        resetState();
        setUseReserve(reservWarehouseId > 0);
        setUseTeam(teamWarehouseId > 0);
        setUseMain(true);
        setAddServices(hasAdditionalItems);
        setSkipExisting(true);
        // Reset only when opening another generation; warehouse flags come from current object.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showTarget?.generationId]);

    const buildServiceRows = (items: ProposalExtraItemType[]): ServicePreviewRow[] => {
        const existingServiceIds = new Set<number>();
        if (skipExisting) {
            const expendTeams = Array.isArray(vObject?.expendTeams) ? vObject.expendTeams : [];
            for (const expend of expendTeams) {
                const status = Number(
                    expend?.income_status ?? expend?.incomeStatus ?? vconst.incomeStatActive,
                );
                if (status === vconst.incomeStatCancel) continue;
                const sid = Number(expend?.serviceId ?? expend?.service_id ?? 0);
                if (sid > 0) existingServiceIds.add(sid);
            }
        }

        return (items || [])
            .map((item, index) => {
                const count = Number(item.itemCount || 0);
                if (!(count > 0)) return null;
                const serviceId = Number(
                    item.serviceId || (item as { service_id?: number }).service_id || 0,
                );
                const asOtherExpense = isProposalExtraAsOtherExpense(item, includeOtherInObjectCost);
                const itemName = String(item.itemName || '').trim() || `#${serviceId || index + 1}`;
                const alreadyOnObject = skipExisting
                    ? asOtherExpense
                        ? hasMatchingOtherExpend(vObject?.expends, itemName)
                        : serviceId > 0 && existingServiceIds.has(serviceId)
                    : false;
                // Новий формат: price = cost, priceSale = sale. Legacy: лише price (= sale).
                const hasSale = item.priceSale != null || item.priceSaleRate != null;
                const priceSale = hasSale ? Number(item.priceSale || 0) : Number(item.price || 0);
                const priceSaleRate = hasSale
                    ? item.priceSaleRate != null
                        ? Number(item.priceSaleRate)
                        : undefined
                    : item.priceRate != null
                      ? Number(item.priceRate)
                      : undefined;
                const price = hasSale ? Number(item.price || 0) : 0;
                const priceRate = hasSale
                    ? item.priceRate != null
                        ? Number(item.priceRate)
                        : undefined
                    : undefined;
                return {
                    key: item.rowId || `service-${index}-${serviceId}`,
                    serviceId,
                    itemName,
                    itemCount: count,
                    price,
                    priceRate,
                    priceSale,
                    priceSaleRate,
                    teamId: teamId || 0,
                    teamLabel: teamId
                        ? teamName || `#${teamId}`
                        : t('documents.proposal2Warehouse.noTeam'),
                    alreadyOnObject,
                    asOtherExpense,
                    selected: !alreadyOnObject,
                } as ServicePreviewRow;
            })
            .filter(Boolean) as ServicePreviewRow[];
    };

    const getEnabledSources = (): WarehouseSourceOption[] => {
        const enabled: WarehouseSourceOption[] = [];
        if (useReserve) {
            const reserve = sourceOptions.find((s) => s.key === 'reserve');
            if (reserve?.available) enabled.push(reserve);
        }
        if (useTeam) {
            const team = sourceOptions.find((s) => s.key === 'team');
            if (team?.available) enabled.push(team);
        }
        if (useCustom) {
            enabled.push(...customSourceOptions.filter((s) => s.available));
        }
        if (useMain) {
            const main = sourceOptions.find((s) => s.key === 'main');
            if (main?.available) enabled.push(main);
        }
        return enabled;
    };

    const handleForm = async () => {
        if (!showTarget) return;
        if (!objectWarehouseId) {
            showToast(t('documents.proposal2Warehouse.messNoObjectWarehouse'), 'danger');
            return;
        }

        const enabledSources = getEnabledSources();
        const wantServices = addServices && hasAdditionalItems;
        if (!enabledSources.length && !wantServices) {
            showToast(t('documents.proposal2Warehouse.messNoSources'), 'warning');
            return;
        }
        if (!hasProducts && !wantServices) {
            showToast(t('documents.proposal2Warehouse.messNoDemand'), 'warning');
            return;
        }

        setForming(true);
        try {
            let nextProducts: ProductPreviewRow[] = [];
            if (hasProducts && enabledSources.length) {
                const { demand, setErrors } = await buildDemand(showTarget.products);
                for (const name of setErrors) {
                    showToast(
                        t('documents.proposal2Warehouse.messSetExpandError', { name }),
                        'danger',
                    );
                }
                if (demand.length) {
                    nextProducts = await allocateFromWarehouses({
                        demand,
                        enabledSources,
                        objectWarehouseId,
                        skipExisting,
                        existingLabel: t('documents.proposal2Warehouse.warehouseExisting'),
                    });
                }
            }

            const nextServices = wantServices
                ? buildServiceRows(showTarget.additionalItems)
                : [];

            if (!nextProducts.length && !nextServices.length) {
                showToast(t('documents.proposal2Warehouse.messNoDemand'), 'warning');
                return;
            }

            setProductRows(nextProducts);
            setServiceRows(nextServices);
            setStep('preview');
        } catch (e) {
            console.error(e);
            showToast(t('documents.proposal2Warehouse.messFormError'), 'danger');
        } finally {
            setForming(false);
        }
    };

    const toggleProduct = (key: string) => {
        setProductRows((prev) =>
            prev.map((row) =>
                row.key === key && !row.alreadyOnObject
                    ? { ...row, selected: !row.selected }
                    : row,
            ),
        );
    };

    const toggleService = (key: string) => {
        setServiceRows((prev) =>
            prev.map((row) =>
                row.key === key && !row.alreadyOnObject
                    ? { ...row, selected: !row.selected }
                    : row,
            ),
        );
    };

    const handleApprove = async () => {
        if (!objectWarehouseId || !objectId) {
            showToast(t('documents.proposal2Warehouse.messNoObjectWarehouse'), 'danger');
            return;
        }

        const selectedProducts = productRows.filter(
            (r) => r.selected && !r.alreadyOnObject && r.transferCount > 0,
        );
        const selectedServices = serviceRows.filter((r) => r.selected && !r.alreadyOnObject);
        if (!selectedProducts.length && !selectedServices.length) {
            showToast(t('documents.proposal2Warehouse.messNothingSelected'), 'warning');
            return;
        }

        setApproving(true);
        let anyOk = false;
        let anyFail = false;
        let salePriceFail = false;
        const salePriceItems: Array<{
            productId: number;
            priceSale: number;
            fromWarehouseId: number;
            count: number;
        }> = [];

        const nextProducts: ProductPreviewRow[] = [];
        for (const row of productRows) {
            if (!row.selected || !(row.transferCount > 0) || !(row.warehouseId > 0)) {
                continue;
            }

            const transferData = await buildTransferItems(
                row.warehouseId,
                row,
                row.transferCount,
                showTarget?.title || '',
                splitByPrice,
                rate,
            );
            if (!transferData?.length) {
                anyFail = true;
                nextProducts.push({
                    ...row,
                    status: 'fail',
                    errorMessage: t('documents.proposal2Warehouse.messNoStock'),
                });
                continue;
            }

            const res = await apiWarehouse.transferBetweenWarehouses(
                row.warehouseId,
                objectWarehouseId,
                transferData,
            );
            if (res?.stat) {
                anyOk = true;
                salePriceItems.push({
                    productId: row.productId,
                    priceSale: row.priceSale,
                    fromWarehouseId: row.warehouseId,
                    count: row.transferCount,
                });
                nextProducts.push({ ...row, status: 'ok' });
            } else {
                anyFail = true;
                nextProducts.push({
                    ...row,
                    status: 'fail',
                    errorMessage: mapTransferError(res?.errCode, t),
                });
            }
        }

        if (salePriceItems.length) {
            const saleApplied = await applyProposalSalePricesBatch(
                objectWarehouseId,
                salePriceItems,
                showTarget?.title || '',
            );
            if (!saleApplied) salePriceFail = true;
        }

        const nextServices: ServicePreviewRow[] = [];
        for (const row of serviceRows) {
            if (!row.selected || row.alreadyOnObject) {
                continue;
            }

            const price = Number(row.price || 0);
            const priceRate = row.priceRate != null ? Number(row.priceRate) : price * rate;
            const priceSale = Number(row.priceSale || 0);
            const priceSaleRate =
                row.priceSaleRate != null ? Number(row.priceSaleRate) : priceSale * rate;

            const resExpend = row.asOtherExpense
                ? await addProposalExtraAsOtherExpend({
                      objectId: Number(objectId),
                      itemName: row.itemName,
                      itemCount: row.itemCount,
                      price,
                      priceRate,
                      priceSale,
                      priceSaleRate,
                  })
                : await apiObject.addExpendTeam({
                      teamId: row.teamId || 0,
                      objectId,
                      serviceId: row.serviceId || 0,
                      name: row.itemName,
                      count: row.itemCount,
                      price,
                      priceRate,
                      priceSale,
                      priceSaleRate,
                      description: showTarget?.title || '',
                  });
            if (resExpend) {
                anyOk = true;
                nextServices.push({ ...row, status: 'ok' });
            } else {
                anyFail = true;
                nextServices.push({
                    ...row,
                    status: 'fail',
                    errorMessage: t('common:messError'),
                });
            }
        }

        setProductRows(nextProducts.filter((r) => r.status));
        setServiceRows(nextServices.filter((r) => r.status));
        setStep('result');

        if (anyOk && fetchObject) {
            await fetchObject();
        }

        if (anyOk && !anyFail) {
            showToast(t('documents.proposal2Warehouse.messApproveOk'), 'success');
        } else if (anyOk && anyFail) {
            showToast(t('documents.proposal2Warehouse.messApprovePartial'), 'warning');
        } else {
            showToast(t('documents.proposal2Warehouse.messApproveFail'), 'danger');
        }

        if (anyOk && salePriceFail) {
            showToast(t('documents.proposal2Warehouse.messSalePricePartial'), 'warning');
        }

        setApproving(false);
    };

    const handleBackToSources = () => {
        setStep('sources');
        setProductRows([]);
        setServiceRows([]);
    };

    const canApprove =
        productRows.some((r) => r.selected && r.transferCount > 0) ||
        serviceRows.some((r) => r.selected && !r.alreadyOnObject);

    return {
        step,
        title: showTarget?.title || t('documents.proposal2Warehouse.title'),
        sourceOptions,
        switchLabels,
        switchHints,
        useReserve,
        useTeam,
        useCustom,
        useMain,
        addServices,
        setUseReserve,
        setUseTeam,
        setUseCustom,
        setUseMain,
        setAddServices,
        skipExisting,
        setSkipExisting,
        hasAdditionalItems,
        productRows,
        serviceRows,
        forming,
        approving,
        canApprove,
        toggleProduct,
        toggleService,
        handleForm,
        handleApprove,
        handleBackToSources,
        handleHide,
    };
};

export default useProposal2Warehouse;
